import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LIMITES } from '../limites';
import { Zap, Award, CheckCircle, XCircle, Loader, Star, Clock, HelpCircle } from 'lucide-react';
import './PiensaRapido.css';

// --- Configuración del Juego ---
const TIME_PER_DIFFICULTY = {
    easy: 30,
    medium: 25,
    hard: 20
};

// Tipos de preguntas que usan input de texto
const TEXT_INPUT_TYPES = [
    'translation_es_en',
    'translation_en_es',
    'antonym_blank',
    'synonym',
    'antonym',
    'word_form',
    'nuance'
];

// Tipos de preguntas que usan botones de opción múltiple
const MULTIPLE_CHOICE_TYPES = [
    'multiple_choice_blank',
    'odd_one_out',
    'context_choice',
    'idiom',
    'phrasal_verb_context',
    'register'
];

function PiensaRapido() {
    // --- Estados ---
    const [gameState, setGameState] = useState('menu');
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [userAnswer, setUserAnswer] = useState('');
    const [score, setScore] = useState(0);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [streak, setStreak] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [difficulty, setDifficulty] = useState(null);
    const [seenPromptsInSession, setSeenPromptsInSession] = useState({});
    const [showCorrectionCard, setShowCorrectionCard] = useState(false);
    const [showHint, setShowHint] = useState(false);

    const inputRef = useRef(null);
    const totalQuestions = questions.length;

    const handleEndGame = useCallback(async () => {
        if (difficulty && questions.length > 0) {
            const newPromptsLearned = questions.map(q => q.prompt);
            setSeenPromptsInSession(prev => {
                const updatedSeen = new Set([...(prev[difficulty] || []), ...newPromptsLearned]);
                return { ...prev, [difficulty]: Array.from(updatedSeen) };
            });
            
            // 🔥 SUTURA CRÍTICA: Enviar la victoria al Backend usando el nuevo endpoint genérico para minijuegos
            try {
                const token = localStorage.getItem('token');
                await fetch('/api/minigames/save-score', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        game_name: 'piensa_rapido',
                        score: score,
                        correct_answers: correctAnswers,
                        total_questions: totalQuestions
                    })
                });
                console.log("¡XP reclamada con éxito para Piensa Rápido!");
            } catch (error) {
                console.error("Error al reclamar XP:", error);
            }
        }
        setGameState('finished');
    }, [questions, difficulty]);

    const loadNextQuestion = useCallback((qNumber, qList, currentDifficulty) => {
        if (qNumber < qList.length) {
            const originalQ = qList[qNumber];
            const nextQ = { ...originalQ };
            if (nextQ.options && nextQ.options.length > 0) {
                // Mezclar las opciones de forma aleatoria (shallow copy de options)
                nextQ.options = [...originalQ.options].sort(() => Math.random() - 0.5);
            }
            
            setCurrentQuestion(nextQ);
            setUserAnswer('');
            setShowHint(false);
            const totalTime = TIME_PER_DIFFICULTY[currentDifficulty];
            setTimeLeft(totalTime);
            setFeedback(null);
            setShowCorrectionCard(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        } else {
            handleEndGame();
        }
    }, [handleEndGame]);

    const moveToNextQuestion = () => {
        const nextQuestionNum = questionNumber + 1;
        setQuestionNumber(nextQuestionNum);
        loadNextQuestion(nextQuestionNum, questions, difficulty);
    };

    const startGame = useCallback((gameQuestions, currentDifficulty) => {
        setScore(0);
        setQuestionNumber(0);
        setStreak(0);
        setCorrectAnswers(0);
        setFeedback(null);
        setGameState('playing');
        loadNextQuestion(0, gameQuestions, currentDifficulty);
    }, [loadNextQuestion]);

    const fetchQuestions = async (selectedDifficulty) => {
        setIsLoading(true);
        setGameState('loading');
        setDifficulty(selectedDifficulty);
        const seenPrompts = seenPromptsInSession[selectedDifficulty] || [];
        try {
            const token = localStorage.getItem('token');
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-piensa-rapido', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    difficulty: selectedDifficulty, 
                    seen_prompts: seenPrompts 
                }),
            });
            if (!response.ok) throw new Error('No se pudieron cargar las preguntas.');
            
            const data = await response.json();
            setQuestions(data);
            startGame(data, selectedDifficulty);
        } catch (error) {
            console.error(error);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswer = useCallback((isTimeOut = false, selectedOption = null) => {
        if (feedback || showCorrectionCard) return;

        const answer = selectedOption !== null ? selectedOption.toLowerCase().trim() : userAnswer.toLowerCase().trim();
        const correctAnswer = currentQuestion.answer.toLowerCase().trim();
        const wasCorrect = !isTimeOut && answer === correctAnswer;

        if (wasCorrect) {
            const timeBonus = Math.max(0, timeLeft * 2);
            const streakBonus = streak * 5;
            const points = 50 + timeBonus + streakBonus;
            setScore(s => s + points);
            setStreak(s => s + 1);
            setCorrectAnswers(c => c + 1);
            setFeedback({ type: 'success', message: `Correct! +${points}` });
            setTimeout(moveToNextQuestion, 1500);
        } else {
            setStreak(0);
            setShowCorrectionCard(true);
        }
    }, [userAnswer, currentQuestion, feedback, timeLeft, streak, showCorrectionCard, moveToNextQuestion]);

    useEffect(() => {
        if (gameState !== 'playing' || feedback || showCorrectionCard) return;

        if (timeLeft <= 0) {
            handleAnswer(true);
        } else {
            const timerId = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
            return () => clearTimeout(timerId);
        }
    }, [timeLeft, gameState, feedback, showCorrectionCard, handleAnswer]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (showCorrectionCard) {
                moveToNextQuestion();
            } else if (userAnswer.trim() && !feedback && TEXT_INPUT_TYPES.includes(currentQuestion?.type)) {
                handleAnswer();
            }
        }
    };

    const toggleHint = () => {
        setShowHint(prev => !prev);
    };

    const renderMultipleChoice = () => {
        return (
            <div className="pr-options-container">
                {currentQuestion.options.map((option, index) => (
                    <button
                        key={index}
                        className="pr-option-btn"
                        onClick={() => handleAnswer(false, option)}
                        disabled={!!feedback || showCorrectionCard}
                    >
                        {option}
                    </button>
                ))}
            </div>
        );
    };

    const renderMenu = () => (
        <div className="pr-game-menu">
            <div className="pr-icon-container"><Zap size={64} /></div>
            <h2>¡Piensa Rápido 2.0!</h2>
            <p>Elige una dificultad para empezar. ¡Cada segundo cuenta para tu puntuación!</p>
            <div className="pr-difficulty-buttons">
                <button onClick={() => fetchQuestions('easy')} disabled={isLoading} className={`pr-btn ${difficulty === 'easy' ? 'active' : ''}`}>Fácil</button>
                <button onClick={() => fetchQuestions('medium')} disabled={isLoading} className={`pr-btn ${difficulty === 'medium' ? 'active' : ''}`}>Medio</button>
                <button onClick={() => fetchQuestions('hard')} disabled={isLoading} className={`pr-btn ${difficulty === 'hard' ? 'active' : ''}`}>Difícil</button>
            </div>
            {isLoading && <button disabled className="pr-start-btn"><Loader className="animate-spin" /> Loading...</button>}
        </div>
    );

    const renderCorrectionCard = () => {
        const isLongAnswer = currentQuestion.answer.length > 25;
        return (
            <div className="pr-feedback-overlay">
                <div className={`pr-feedback-card ${isLongAnswer ? 'long-answer' : ''}`}>
                    <XCircle size={48} className="error" />
                    <h2>Incorrect Answer!</h2>
                    <p>The correct answer is:</p>
                    <strong>{currentQuestion.answer}</strong>
                    {currentQuestion.feedback && (
                        <>
                            <p><strong>Common Mistake:</strong> {currentQuestion.feedback.common_mistake}</p>
                            <p><strong>Explanation:</strong> {currentQuestion.feedback.explanation}</p>
                        </>
                    )}
                    <button className="pr-continue-btn" onClick={moveToNextQuestion}>Continue</button>
                </div>
            </div>
        );
    };

    const renderGame = () => {
        const totalTimeForBar = TIME_PER_DIFFICULTY[difficulty] || 30;
        const timerProgress = (timeLeft / totalTimeForBar) * 100;
        const isTextInput = TEXT_INPUT_TYPES.includes(currentQuestion.type);

        return (
            <div className="pr-game-view">
                {showCorrectionCard && renderCorrectionCard()}
                {showHint && (
                    <div className="pr-hint-overlay">
                        <div className="pr-hint-card">
                            <h3>Hint</h3>
                            <p>{currentQuestion.hint}</p>
                            <button className="pr-start-btn" onClick={toggleHint}>Close</button>
                        </div>
                    </div>
                )}
                <div className="pr-game-header">
                    <div className="pr-stat"><Star size={18} /><span>Score: <strong>{score}</strong></span></div>
                    <div className="pr-stat"><Zap size={18} /><span>Streak: <strong className="pr-streak">{streak}</strong></span></div>
                    <div className={`pr-timer ${timeLeft <= 5 ? 'warning' : ''}`}><Clock size={18} /><span>{timeLeft}s</span></div>
                </div>
                <div className="pr-progress-bar-container">
                    <div className="pr-progress-bar" style={{ width: `${timerProgress}%` }}></div>
                </div>
                <div className="pr-question-card">
                    <div className="pr-question-type">{currentQuestion.type.replace(/_/g, ' ').toUpperCase()}</div>
                    <h3>{currentQuestion.prompt}</h3>
                    {isTextInput ? (
                        <>
                            <input
                                maxLength={LIMITES.respuestaMinijuego}
                                ref={inputRef}
                                type="text"
                                value={userAnswer}
                                onChange={(e) => setUserAnswer(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your answer..."
                                disabled={!!feedback || showCorrectionCard}
                                className="pr-answer-input"
                            />
                            <button
                                onClick={() => handleAnswer()}
                                disabled={!userAnswer.trim() || !!feedback || showCorrectionCard}
                                className="pr-confirm-btn"
                            >
                                Confirm
                            </button>
                        </>
                    ) : (
                        renderMultipleChoice()
                    )}
                    <button className="pr-hint-btn" onClick={toggleHint} disabled={showHint || showCorrectionCard}>
                        <HelpCircle size={18} /> Hint
                    </button>
                </div>
                {feedback && feedback.type === 'success' && (
                    <div className={`pr-feedback ${feedback.type}`}>
                        <CheckCircle />
                        {feedback.message}
                    </div>
                )}
            </div>
        );
    };

    const renderFinished = () => {
        const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
        return (
            <div className="pr-game-menu">
                <div className="pr-icon-container"><Award size={64} /></div>
                <h2>Game Finished!</h2>
                <div className="pr-stats-preview final">
                    <div><span>{score}</span> Points</div>
                    <div><span>{correctAnswers}/{totalQuestions}</span> Correct</div>
                    <div><span>{accuracy}%</span> Accuracy</div>
                </div>
                <button onClick={() => setGameState('menu')} className="pr-start-btn">Play Again</button>
            </div>
        );
    };

    return (
        <div className="piensa-rapido-container">
            <div className={`piensa-rapido-inner-container ${gameState}`}>
                {gameState === 'menu' && renderMenu()}
                {gameState === 'loading' && <div className="pr-loading-view"><Loader className="animate-spin" size={48} /><p>Loading questions...</p></div>}
                {gameState === 'playing' && currentQuestion && renderGame()}
                {gameState === 'finished' && renderFinished()}
            </div>
        </div>
    );
}

export default PiensaRapido;